import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Car, TrendingDown, CheckCircle, Loader2, RefreshCw, MoreHorizontal, Heart, Send, Info, ToggleRight } from 'lucide-react';
import EventStoryForm from './EventStoryForm';
import storyApi, { generateDemoPreview } from '../../../services/storyApi';
import { STORY_EVENT_TYPES, DEFAULT_PUBLICATION_EVENTS } from '../../../constants/settings';
import useTemplateManifest from '../../../hooks/useTemplateManifest';

// Icon mapping for event tabs
const EVENT_ICONS = { Car, TrendingDown, CheckCircle };

/**
 * BackendPreview - Shows actual backend-generated preview
 * This ensures Settings preview matches publication exactly
 */
const BackendPreview = ({ eventType, refreshTrigger }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventType, refreshTrigger]);

  const loadPreview = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await generateDemoPreview(eventType);
      // Add cache buster to force reload
      setPreviewUrl(result.preview_url + '?t=' + Date.now());
    } catch (err) {
      console.error('Error loading preview:', err);
      setError('Erreur de preview');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-dark-hover">
        <Loader2 className="h-6 w-6 animate-spin text-carmin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-dark-hover text-muted text-sm">
        {error}
      </div>
    );
  }

  return (
    <img
      src={previewUrl}
      alt="Story preview"
      className="w-full h-full object-contain"
    />
  );
};

/**
 * TabStories - Story settings with backend-generated preview
 * Redesigned with clearer event tabs and larger preview panel
 */
const TabStories = ({ settings, onChange }) => {
  const [activeEventTab, setActiveEventTab] = useState('new_vehicle');
  const [storyConfigs, setStoryConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [previewTrigger, setPreviewTrigger] = useState(0);
  const saveTimeoutRef = useRef(null);
  const { manifest } = useTemplateManifest();

  // Load all story configs on mount
  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    setLoading(true);
    setError('');
    try {
      const configs = await storyApi.getAllConfigs();
      setStoryConfigs(configs);
    } catch (err) {
      console.error('Error loading story configs:', err);
      setError('Erreur lors du chargement des configurations');
    } finally {
      setLoading(false);
    }
  };

  // Handle config update with debounced save and preview refresh
  const handleConfigChange = useCallback((eventType, updatedConfig) => {
    // Update local state immediately for responsive UI
    setStoryConfigs(prev => ({
      ...prev,
      [eventType]: updatedConfig,
    }));

    // Debounce the save and preview refresh
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    setSaving(true);
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await storyApi.updateConfig(eventType, updatedConfig);
        // Refresh preview after save
        setPreviewTrigger(prev => prev + 1);
      } catch (err) {
        console.error('Error saving config:', err);
      } finally {
        setSaving(false);
      }
    }, 500); // 500ms debounce
  }, []);

  // Manual preview refresh
  const refreshPreview = () => {
    setPreviewTrigger(prev => prev + 1);
  };

  // Get current event config
  const currentConfig = storyConfigs[activeEventTab];
  const currentEventConfig = STORY_EVENT_TYPES.find(e => e.id === activeEventTab);

  // Check if instagram_story is enabled for this event type
  const pubEvents = settings?.publication_events || DEFAULT_PUBLICATION_EVENTS;
  const isStoryEnabled = pubEvents[activeEventTab]?.instagram_story !== false;

  // Toggle instagram_story for current event
  const toggleStoryForEvent = () => {
    const current = settings?.publication_events || DEFAULT_PUBLICATION_EVENTS;
    const eventConfig = current[activeEventTab] || {};
    onChange({
      ...settings,
      publication_events: {
        ...current,
        [activeEventTab]: { ...eventConfig, instagram_story: !eventConfig.instagram_story },
      },
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-carmin" />
        <span className="ml-3 text-secondary">Chargement des configurations...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400 mb-4">{error}</p>
        <button onClick={loadConfigs} className="text-carmin hover:text-carmin-light">
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr,300px] gap-6">
      {/* Controls */}
      <div className="space-y-5 min-w-0">
        {/* Event type tabs - pill-group */}
        <div>
          <div className="flex bg-dark-hover/50 rounded-lg p-1">
            {STORY_EVENT_TYPES.map((eventType) => {
              const Icon = EVENT_ICONS[eventType.icon];
              const isActive = activeEventTab === eventType.id;
              return (
                <button
                  key={eventType.id}
                  onClick={() => setActiveEventTab(eventType.id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-dark-card text-primary shadow-sm'
                      : 'text-secondary hover:text-primary'
                  }`}
                >
                  {Icon && <Icon className="h-3.5 w-3.5" />}
                  {eventType.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Story activation status */}
        <div
          key={activeEventTab}
          className={`flex items-start gap-2.5 px-3.5 py-2.5 rounded-lg text-[13px] leading-relaxed transition-all animate-[fadeIn_0.2s_ease-out] ${
            isStoryEnabled
              ? 'bg-emerald-500/5 border border-emerald-500/15 text-emerald-400/90'
              : 'bg-amber-500/5 border border-amber-500/15 text-amber-400/90'
          }`}
        >
          {isStoryEnabled ? (
            <Info className="h-4 w-4 mt-0.5 flex-shrink-0 opacity-70" />
          ) : (
            <Info className="h-4 w-4 mt-0.5 flex-shrink-0 opacity-70" />
          )}
          <div className="flex-1 min-w-0">
            {isStoryEnabled ? (
              <span>
                La création de stories est <strong className="font-medium text-emerald-400">activée</strong> pour l'événement <strong className="font-medium text-emerald-400">{currentEventConfig?.label?.toLowerCase()}</strong>. Les visuels seront générés automatiquement.
              </span>
            ) : (
              <span>
                Les stories ne sont pas activées pour cet événement. Vos réglages sont sauvegardés mais aucune story ne sera générée.{' '}
                <button
                  onClick={toggleStoryForEvent}
                  className="inline-flex items-center gap-1 text-amber-300 hover:text-amber-200 font-medium underline underline-offset-2 decoration-amber-300/30 hover:decoration-amber-200/50 transition-colors"
                >
                  <ToggleRight className="h-3.5 w-3.5" />
                  Activer les stories
                </button>
              </span>
            )}
          </div>
        </div>

        {/* Event-specific form */}
        {currentConfig && (
          <EventStoryForm
            eventType={activeEventTab}
            config={currentConfig}
            onChange={(updatedConfig) => handleConfigChange(activeEventTab, updatedConfig)}
            manifest={manifest}
          />
        )}
      </div>

      {/* Live Preview - larger, with phone frame */}
      <div className="lg:sticky lg:top-20 self-start h-fit">
        <div className="bg-dark-hover border border-dark-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <label className="text-sm font-medium text-primary block">
                Aperçu
              </label>
              <span className="text-[11px] text-muted">
                {currentEventConfig?.label || 'Nouvelle voiture'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {saving && <span className="text-green-400 animate-pulse text-xs">Sauvegarde...</span>}
              <button
                onClick={refreshPreview}
                className="p-1.5 text-secondary hover:text-primary bg-dark-surface rounded-lg transition-colors"
                title="Actualiser"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Phone frame */}
          <div className="flex justify-center">
            <div className="relative">
              {/* Phone notch */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-4 bg-dark-hover rounded-b-xl z-10" />
              {/* Screen */}
              <div style={{
                width: 250,
                height: 444,
                borderRadius: 24,
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 0 0 2px rgba(255,255,255,0.05)',
                border: '3px solid #2a2a35',
                position: 'relative',
              }}>
                <BackendPreview
                  eventType={activeEventTab}
                  refreshTrigger={previewTrigger}
                />
                {/* Instagram Story overlay */}
                <div className="absolute inset-0 pointer-events-none flex flex-col justify-between" style={{ zIndex: 2 }}>
                  {/* Top: progress bars + profile */}
                  <div>
                    <div className="flex gap-1 px-2.5 pt-3">
                      <div className="flex-1 h-[2px] rounded-full bg-white" />
                      <div className="flex-1 h-[2px] rounded-full bg-white/30" />
                      <div className="flex-1 h-[2px] rounded-full bg-white/30" />
                    </div>
                    <div className="flex items-center gap-2 px-2.5 mt-2">
                      <div className="w-7 h-7 rounded-full bg-gray-400/60 border border-white/40 flex-shrink-0" />
                      <span className="text-white text-[11px] font-semibold drop-shadow-sm truncate">
                        {settings?.company_name || 'votre_garage'}
                      </span>
                      <span className="text-white/60 text-[11px] drop-shadow-sm flex-shrink-0">2h</span>
                      <MoreHorizontal className="ml-auto h-4 w-4 text-white/80 drop-shadow-sm flex-shrink-0" />
                    </div>
                  </div>
                  {/* Bottom: message input + icons */}
                  <div className="px-2.5 pb-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-8 rounded-full border border-white/30 flex items-center px-3">
                        <span className="text-white/50 text-[10px]">Envoyer un message</span>
                      </div>
                      <Heart className="h-5 w-5 text-white/80 flex-shrink-0" />
                      <Send className="h-5 w-5 text-white/80 flex-shrink-0" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p className="mt-3 text-[11px] text-muted text-center">
            Résultat identique à la publication
          </p>
        </div>
      </div>
    </div>
  );
};

export default TabStories;
